import { Field, getIn, useFormikContext } from 'formik'
import isEqual from 'lodash/isEqual'
import React from 'react'
import { useNavigate, useParams } from 'react-router-dom'

import { useDashboardFeatureFlags } from '@components'
import { FeatureFlagDecorator } from '@components/utils/FeatureFlagDecorator'
import { Button, ButtonClean, Subsection, Tooltip, useModalContext } from '@fourthwall/components'
import { PaymentMethodRequiredModal } from '@modules/Dashboard/modules/PaymentMethods'
import { useIsPaymentMethodModalRequiredMutation } from '@mutations/useIsPaymentMethodModalRequiredMutation'
import { routing } from '@utils/routing'

import { useOfferQuery } from '../../../../hooks'
import type { ProductFormState } from '../../../SingleProduct.types'
import { PackageSizeInputFormik } from '../shared/PackageSizeSelect/PackageSizeInput'
import { useShippingChoiceModal } from '../shared/SelfOrAssistedShippingChoiceModal'
import { ShippingStartDate } from '../shared/ShippingStartDate/ShippingStartDate'
import { TaxFields } from '../shared/TaxFields'
import { Shipping3plDescription } from './Description/Shipping3plDescription'
import { InventoryShipmentSection } from './InventoryShipmentSection/InventoryShipmentSection'
import * as S from './styled'

export const ProductShipping3PL = () => {
  const { open } = useModalContext()
  const { productId = '' } = useParams<'productId'>()
  const navigate = useNavigate()
  const { errors, values, initialValues } = useFormikContext<ProductFormState>()
  const isFormDirty = !isEqual(initialValues, values)
  const { dashboardFeatureFlags } = useDashboardFeatureFlags()
  const isShippingDisabled =
    getIn(errors, 'customsInformation.shortHsCode') ||
    getIn(errors, 'customsInformation.countryOfOrigin') ||
    getIn(errors, 'shippingPackageSize') ||
    isFormDirty

  const { offerQuery } = useOfferQuery([productId])
  const { shippingConfig } = offerQuery?.data?.config || {}
  const { openShippingModal } = useShippingChoiceModal()

  const { isPaymentMethodModalRequiredMutation } = useIsPaymentMethodModalRequiredMutation()

  const { isShippingTypeEditable } = shippingConfig || {}

  const linkToCreateInventoryShipment = {
    pathname: routing.settings.shipping.inventoryShipments.create,
    search: `?offerId=${productId}`,
  }

  const openPaymentMethodRequiredModal = async () => {
    return isPaymentMethodModalRequiredMutation.mutateAsync([], {
      onSuccess: (data) => {
        if (data.isPaymentMethodModalRequired) {
          open(PaymentMethodRequiredModal, {
            title: 'Payment method required',
            subtitle: (
              <p>
                A payment method is required to use our shipping service. Your card will only be
                charged if your account balance does not cover a fulfillment cost (such as{' '}
                <u>monthly storage</u> and <u>per order fulfillment fees)</u>.
              </p>
            ),
            onSuccess: () => {
              navigate(linkToCreateInventoryShipment)
            },
          })
        } else {
          navigate(linkToCreateInventoryShipment)
        }
      },
    })
  }

  const sendButton = () => {
    return (
      <Button
        type="button"
        fullWidth
        label="Send inventory to our fulfillment center"
        disabled={isShippingDisabled}
        href={`${linkToCreateInventoryShipment.pathname}${linkToCreateInventoryShipment.search}`}
        onClick={async (e) => {
          e.preventDefault()
          await openPaymentMethodRequiredModal()
        }}
      />
    )
  }

  return (
    <>
      <Subsection title="Shipping">
        <S.Container>
          <S.ShippingSubtitle>
            <strong>We’ll handle shipping for you</strong>

            {isShippingTypeEditable && (
              <ButtonClean label="Change" onClick={() => openShippingModal()} />
            )}
          </S.ShippingSubtitle>

          <Shipping3plDescription shippingConfig={shippingConfig} />

          <TaxFields showLearnMoreLinks />

          <Field
            component={PackageSizeInputFormik}
            shippingConfig={shippingConfig}
            name="shippingPackageSize"
            label="Product size"
          />

          {isShippingDisabled ? (
            <Tooltip reference={sendButton()} placement="top">
              <div>
                Please save your changes before sending your inventory to our fulfillment center.
              </div>
            </Tooltip>
          ) : (
            sendButton()
          )}
        </S.Container>
      </Subsection>

      <FeatureFlagDecorator {...dashboardFeatureFlags.hsCodes} wrapper={Subsection}>
        <ShippingStartDate />
      </FeatureFlagDecorator>

      <Subsection>
        <InventoryShipmentSection />
      </Subsection>
    </>
  )
}
